import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { Loader2, Upload, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface SwapRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  restaurantId: string;
  vendorId: string;
  menuItems: Array<{ id: string; name: string; price: number }>;
}

export const SwapRequestDialog = ({
  open,
  onOpenChange,
  restaurantId,
  vendorId,
  menuItems,
}: SwapRequestDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    currentDeviceBrand: "",
    currentDeviceModel: "",
    storageCapacity: "",
    batteryHealth: "",
    deviceCondition: "",
    additionalNotes: "",
    targetMenuItemId: "",
  });

  const [images, setImages] = useState({
    frontImage: null as File | null,
    backImage: null as File | null,
    additionalImages: [] as File[],
  });

  const [imagePreviews, setImagePreviews] = useState({
    frontImage: "",
    backImage: "",
    additionalImages: [] as string[],
  });

  const handleImageSelect = (type: "front" | "back" | "additional", files: FileList | null) => {
    if (!files || files.length === 0) return;

    if (type === "additional") {
      const newFiles = Array.from(files);
      setImages(prev => ({
        ...prev,
        additionalImages: [...prev.additionalImages, ...newFiles],
      }));

      newFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => ({
            ...prev,
            additionalImages: [...prev.additionalImages, reader.result as string],
          }));
        };
        reader.readAsDataURL(file);
      });
    } else {
      const file = files[0];
      setImages(prev => ({
        ...prev,
        [type === "front" ? "frontImage" : "backImage"]: file,
      }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => ({
          ...prev,
          [type === "front" ? "frontImage" : "backImage"]: reader.result as string,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeAdditionalImage = (index: number) => {
    setImages(prev => ({
      ...prev,
      additionalImages: prev.additionalImages.filter((_, i) => i !== index),
    }));
    setImagePreviews(prev => ({
      ...prev,
      additionalImages: prev.additionalImages.filter((_, i) => i !== index),
    }));
  };

  const uploadImage = async (file: File, path: string): Promise<string> => {
    const fileExt = file.name.split(".").pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `${path}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("post-media")
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from("post-media")
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to submit a swap request",
        variant: "destructive",
      });
      return;
    }

    // Validation
    if (!images.frontImage || !images.backImage) {
      toast({
        title: "Images required",
        description: "Please upload front and back images of your device",
        variant: "destructive",
      });
      return;
    }

    if (!formData.targetMenuItemId) {
      toast({
        title: "Select device",
        description: "Please select the device you want to swap to",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setUploadingImages(true);

    try {
      // Upload images
      const frontImageUrl = await uploadImage(images.frontImage, "swap-requests");
      const backImageUrl = await uploadImage(images.backImage, "swap-requests");
      
      const additionalImageUrls: string[] = [];
      for (const img of images.additionalImages) {
        const url = await uploadImage(img, "swap-requests");
        additionalImageUrls.push(url);
      }

      setUploadingImages(false);

      // Create swap request
      const { error } = await supabase.from("swap_requests").insert({
        customer_id: user.id,
        restaurant_id: restaurantId,
        vendor_id: vendorId,
        target_menu_item_id: formData.targetMenuItemId,
        current_device_brand: formData.currentDeviceBrand,
        current_device_model: formData.currentDeviceModel,
        storage_capacity: formData.storageCapacity,
        battery_health: formData.batteryHealth,
        device_condition: formData.deviceCondition,
        additional_notes: formData.additionalNotes,
        front_image_url: frontImageUrl,
        back_image_url: backImageUrl,
        additional_images: additionalImageUrls,
      });

      if (error) throw error;

      toast({
        title: "Swap request submitted",
        description: "The vendor will review your device and make an offer soon",
      });

      onOpenChange(false);
      navigate("/swap-requests");
    } catch (error) {
      console.error("Error submitting swap request:", error);
      toast({
        title: "Error",
        description: "Failed to submit swap request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setUploadingImages(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl">
        <DialogHeader>
          <DialogTitle>Device Swap Request</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Current Device Info */}
          <div className="space-y-4">
            <h3 className="font-semibold">Current Device Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="brand">Brand *</Label>
                <Input
                  id="brand"
                  required
                  value={formData.currentDeviceBrand}
                  onChange={(e) => setFormData(prev => ({ ...prev, currentDeviceBrand: e.target.value }))}
                  placeholder="e.g., Apple, Samsung"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="model">Model *</Label>
                <Input
                  id="model"
                  required
                  value={formData.currentDeviceModel}
                  onChange={(e) => setFormData(prev => ({ ...prev, currentDeviceModel: e.target.value }))}
                  placeholder="e.g., iPhone 12, Galaxy S21"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="storage">Storage Capacity *</Label>
              <Select
                value={formData.storageCapacity}
                onValueChange={(value) => setFormData(prev => ({ ...prev, storageCapacity: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select storage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="64GB">64GB</SelectItem>
                  <SelectItem value="128GB">128GB</SelectItem>
                  <SelectItem value="256GB">256GB</SelectItem>
                  <SelectItem value="512GB">512GB</SelectItem>
                  <SelectItem value="1TB">1TB</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="battery">Battery Health *</Label>
              <Select
                value={formData.batteryHealth}
                onValueChange={(value) => setFormData(prev => ({ ...prev, batteryHealth: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select battery health" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Excellent (90-100%)">Excellent (90-100%)</SelectItem>
                  <SelectItem value="Good (80-89%)">Good (80-89%)</SelectItem>
                  <SelectItem value="Fair (70-79%)">Fair (70-79%)</SelectItem>
                  <SelectItem value="Poor (Below 70%)">Poor (Below 70%)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="condition">Device Condition *</Label>
              <Select
                value={formData.deviceCondition}
                onValueChange={(value) => setFormData(prev => ({ ...prev, deviceCondition: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select condition" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Excellent">Excellent (Like new)</SelectItem>
                  <SelectItem value="Good">Good (Minor wear)</SelectItem>
                  <SelectItem value="Fair">Fair (Visible scratches/dents)</SelectItem>
                  <SelectItem value="Poor">Poor (Significant damage)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={formData.additionalNotes}
                onChange={(e) => setFormData(prev => ({ ...prev, additionalNotes: e.target.value }))}
                placeholder="Any additional information about your device..."
                rows={3}
              />
            </div>
          </div>

          {/* Target Device */}
          <div className="space-y-4">
            <h3 className="font-semibold">Device You Want *</h3>
            <Select
              value={formData.targetMenuItemId}
              onValueChange={(value) => setFormData(prev => ({ ...prev, targetMenuItemId: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select device" />
              </SelectTrigger>
              <SelectContent>
                {menuItems.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name} - D{item.price}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Image Uploads */}
          <div className="space-y-4">
            <h3 className="font-semibold">Device Photos *</h3>
            
            {/* Front Image */}
            <div className="space-y-2">
              <Label>Front of Device</Label>
              {imagePreviews.frontImage ? (
                <div className="relative w-full h-40 border rounded-lg overflow-hidden">
                  <img src={imagePreviews.frontImage} alt="Front" className="w-full h-full object-cover" />
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    className="absolute top-2 right-2"
                    onClick={() => {
                      setImages(prev => ({ ...prev, frontImage: null }));
                      setImagePreviews(prev => ({ ...prev, frontImage: "" }));
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-500">Click to upload front image</span>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleImageSelect("front", e.target.files)}
                  />
                </label>
              )}
            </div>

            {/* Back Image */}
            <div className="space-y-2">
              <Label>Back of Device</Label>
              {imagePreviews.backImage ? (
                <div className="relative w-full h-40 border rounded-lg overflow-hidden">
                  <img src={imagePreviews.backImage} alt="Back" className="w-full h-full object-cover" />
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    className="absolute top-2 right-2"
                    onClick={() => {
                      setImages(prev => ({ ...prev, backImage: null }));
                      setImagePreviews(prev => ({ ...prev, backImage: "" }));
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-500">Click to upload back image</span>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleImageSelect("back", e.target.files)}
                  />
                </label>
              )}
            </div>

            {/* Additional Images */}
            <div className="space-y-2">
              <Label>Additional Images (Optional)</Label>
              <div className="grid grid-cols-3 gap-2">
                {imagePreviews.additionalImages.map((preview, index) => (
                  <div key={index} className="relative aspect-square border rounded-lg overflow-hidden">
                    <img src={preview} alt={`Additional ${index + 1}`} className="w-full h-full object-cover" />
                    <Button
                      type="button"
                      size="icon"
                      variant="destructive"
                      className="absolute top-1 right-1 h-6 w-6"
                      onClick={() => removeAdditionalImage(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                {imagePreviews.additionalImages.length < 5 && (
                  <label className="aspect-square border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 flex flex-col items-center justify-center">
                    <Upload className="h-6 w-6 text-gray-400" />
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleImageSelect("additional", e.target.files)}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {uploadingImages ? "Uploading images..." : "Submitting..."}
                </>
              ) : (
                "Submit Request"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};